import {all, call, cancel, delay, fork, put, select, take, takeLatest} from 'redux-saga/effects'
import types from '../actions/types'
import {get, isEmpty, isNil} from 'lodash'
import {createPivotTableWatcher} from './pivotTable'
import * as settingActions from '../actions/settings'
import * as pivotTableActions from '../actions/pivotTable'
import {apiErrorCodes} from 'helpers/constants'
import {getAllFields} from 'helpers/fields'
import {generalError} from '../actions'
import * as snackbarActions from 'actions/snackbar'
import {snackbarClear, snackbarMessage} from 'actions/snackbar'
import {getFileFormConfig, getFileFormData, validateFileType} from 'helpers/documents'
import {findField} from 'helpers/import'
import SKILLS from 'helpers/onboarding/skills'
import onboardingActions from 'actions/onboarding'
import {getUser} from 'selectors/auth'
import iconNames from 'theme/icons'
import {getAddExternalUserDialog, getAddUserDialog} from 'selectors/dialog'
import {hideAddExternalUserDialog, hideAddUserDialog} from 'actions/dialog'
import {isTrialAccountType} from 'helpers/user'
import {getAccountPackages, getPackages} from 'selectors/data'
import {getContacts} from 'selectors/settings'
import {trustedDevicesPivotTableKey} from "scenes/settingsGeneralScene/trustedDevicesPivotTable"
import {
  addAutomaticReminderConfigFailure,
  addAutomaticReminderConfigSuccess,
  deleteAutomaticReminderConfigFailure,
  deleteAutomaticReminderConfigSuccess,
  readAutomaticReminderConfigsFailure,
  readAutomaticReminderConfigsSuccess,
  resetTfaFailure,
  resetTfaSuccess,
  updateAutomaticReminderConfigFailure,
  updateAutomaticReminderConfigSuccess,
} from 'actions/settings'
import {
  getFilteredAccountPackages,
  getSubscriptionParamsForTrial,
  isSubscriptionUpdateRequiredForTrial,
} from 'helpers/settings'
import {
  getExternalUsers,
  getInternalUsers,
  getLicences,
  getPackages as getPackagesFromSettings,
  getPermissions,
  isUpdatingLogins,
} from 'selectors/settingsContext'
import {
  getAllContacts,
  getDirtyContacts,
  getLicenceIds,
  getPackageIds,
  getUserLicences,
  getUserPackages,
  updateContactsLicencesAndPackages,
  updateLicences,
  updatePackages,
} from 'helpers/logins'
import {
  addDocumentFolderTemplateFailure,
  addDocumentFolderTemplateSuccess,
  deleteDocumentFolderTemplateFailure,
  deleteDocumentFolderTemplateSuccess,
  loadDocumentFolderTemplatesFailure,
  loadDocumentFolderTemplatesSuccess,
  updateDocumentFolderTemplateFailure,
  updateDocumentFolderTemplateSuccess,
} from 'actions/documents'
import {
  closeTfaSetup,
  deleteTrustedDeviceFailure,
  deleteTrustedDeviceSuccess,
  dismissTfaPromptFailure,
  dismissTfaPromptSuccess,
} from 'actions/settings'
import {
  addTeamFailure,
  addTeamSuccess,
  addUserFailure,
  addUserSuccess,
  deleteTeamFailure,
  deleteTeamSuccess,
  loadTeamMembersFailure,
  loadTeamMembersSuccess,
  loadTeamsFailure,
  loadTeamsSuccess,
  removeUserFailure,
  removeUserSuccess,
  sendResetPasswordEmailFailure,
  sendResetPasswordEmailSuccess,
  setLicences,
  updateTeamFailure,
  updateTeamMemberFailure,
  updateTeamMemberSuccess,
  updateTeamSuccess,
  updateUserFailure,
  updateUserSuccess,
} from 'actions/settings'
import {
  addSupportTokenFailure,
  addSupportTokenSuccess, deleteSupportTokenFailure, deleteSupportTokenSuccess,
  loadSupportTokensFailure,
  loadSupportTokensSuccess,
} from "actions/settings"
const contactImportPivotTableKey = 'settingsContext.contactImport.pivotTable'
const USER_UPDATE_DEBOUNCE_DELAY = 1000

export default (api) => {

  function* waitFor(selector) {
    if (yield select(selector)) return

    while (true) {
      yield take('*')
      if (yield select(selector)) return
    }
  }

  function* loadAccountLicenceInfo() {
    try {
      const params = {}
      const fields = {}

      yield call(waitFor, state => !isUpdatingLogins(state))

      const response = yield call(api.accountLicences.get, params, fields)
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(settingActions.loadAccountLicenceInfoSuccess(data['result'][0]))
        },
        function* onFail(key, message, code) {
          yield put(settingActions.loadAccountLicenceInfoFailure(message, code))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* sendActivationEmail({userId}) {
    try {

      const response = yield call(api.settings.sendActivationEmail, userId)
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(settingActions.sendActivationEmailSuccess(data))
          yield put(snackbarMessage('Activation email sent', iconNames.tick))
        },
        function* onFail(key, message, code) {
          yield put(settingActions.sendActivationEmailFailure(message, code))
          yield put(snackbarMessage(message, 'error'))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* sendTFASetupEmail({userId}) {
    try {

      const response = yield call(api.settings.sendTFASetupEmail, userId)
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(settingActions.sendTFASetupEmailSuccess(data))
          yield put(snackbarMessage('TFA Setup email sent', iconNames.tick))
        },
        function* onFail(key, message, code) {
          yield put(settingActions.sendActivationEmailFailure(message, code))
          yield put(snackbarMessage(message, 'error'))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* changePassword({current, password}) {
    try {
      const response = yield call(api.settings.changePassword, current, password)
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(settingActions.sendActivationEmailSuccess(data))
          yield put(snackbarMessage('Your password has been changed', iconNames.tick))
        },
        function* onFail(key, message, code) {
          yield put(settingActions.sendActivationEmailFailure(message, code))
          yield put(snackbarMessage(message, 'error'))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* userRoleUpdate({roleId, userId}) {
    try {
      const params = {
        roleId,
        userId,
      }

      const fields = {}

      const response = yield call(api.settings.userRoleUpdate, params, fields)
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(settingActions.userRoleUpdateSuccess(data.result[0]))
          yield put(snackbarMessage('Role updated'))
        },
        function* onFail(key, message, code) {
          yield put(settingActions.userRoleUpdateFailure(message, code))
          yield put(snackbarMessage('Role update failed'))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  const allowedImageExtensions = ['png', 'jpg']

  function* uploadSignature({file}) {
    try {
      // Check the file type
      if (!validateFileType(file, allowedImageExtensions)) {
        const problem = 'Only png and jpg images are supported'
        yield put(settingActions.uploadSignatureFailure(problem))
        yield put(snackbarMessage('Sorry, your signature could not be saved: ' + problem, 'error', false))
        return
      }

      yield put(snackbarMessage('Uploading your signature...', 'loading', false, false))
      const response = yield call(api.settings.uploadSignature, getFileFormData(file), getFileFormConfig())
      if (response.ok) {
        const data = response.data.data.result[0]
        yield put(settingActions.uploadSignatureSuccess(data))
        yield put(snackbarMessage('Your new signature has been saved', iconNames.tick))
      } else {
        const problem = response.headers['error'] || response.problem
        yield put(settingActions.uploadSignatureFailure(problem))
        yield put(snackbarMessage('Sorry, your signature could not be saved: ' + problem, 'error', false))
      }
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* uploadLogo({file}) {
    try {

      // Check the file type
      if (!validateFileType(file, allowedImageExtensions)) {
        const problem = 'Only png and jpg images are supported'
        yield put(settingActions.uploadLogoFailure(problem))
        yield put(snackbarMessage('Sorry, your logo could not be saved: ' + problem, 'error', false))
        return
      }

      yield put(snackbarMessage('Uploading your company logo...', 'loading', false, false))
      const response = yield call(api.settings.uploadLogo, getFileFormData(file), getFileFormConfig())
      if (response.ok) {
        const data = response.data.data.result[0]
        yield put(settingActions.uploadLogoSuccess(data))
        yield put(snackbarMessage('Your new company logo has been saved', iconNames.tick))
      } else {
        const problem = response.headers['error'] || response.problem
        yield put(settingActions.uploadLogoFailure(problem))
        yield put(snackbarMessage('Sorry, your logo could not be saved: ' + problem, 'error', false))
      }
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* deleteSignature() {
    try {
      const response = yield call(api.settings.deleteSignature)
      if (response.ok) {
        yield put(settingActions.deleteSignatureSuccess())
        yield put(snackbarMessage('Your signature has been removed', iconNames.tick))
      } else {
        const problem = response.headers['error'] || response.problem
        yield put(settingActions.deleteSignatureFailure(problem))
        yield put(snackbarMessage(problem, 'error'))
      }
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* deleteLogo() {
    try {
      const response = yield call(api.settings.deleteLogo)
      if (response.ok) {
        yield put(settingActions.deleteLogoSuccess())
        yield put(snackbarMessage('Your company logo has been removed', iconNames.tick))
      } else {
        const problem = response.headers['error'] || response.problem
        yield put(settingActions.deleteLogoFailure(problem))
        yield put(snackbarMessage(problem, 'error'))
      }
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* addRolePermission({roleId, permissionId}) {
    try {
      const params = {
        roleId,
        permissionId,
      }
      const fields = {}

      const response = yield call(api.settings.addRolePermission, params, fields)
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(settingActions.addRolePermissionSuccess(data['result'][0]))
        },
        function* onFail(key, message, code) {
          yield put(settingActions.addRolePermissionFailure(message, code))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* deleteRolePermission({roleId, permissionId}) {
    try {
      const params = {
        roleId,
        permissionId,
      }
      const fields = {}

      const response = yield call(api.settings.deleteRolePermission, params, fields)
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(settingActions.deleteRolePermissionSuccess(data['result'][0]))
        },
        function* onFail(key, message, code) {
          yield put(settingActions.deleteRolePermissionFailure(message, code))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* addRole({name}) {
    try {
      const params = {
        name,
      }
      const fields = {}

      const response = yield call(api.settings.addRole, params, fields)
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(settingActions.addRoleSuccess(data['result'][0]))
        },
        function* onFail(key, message, code) {
          yield put(settingActions.addRoleFailure(message, code))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* deleteRole({id}) {
    try {
      const params = {
        id,
      }
      const fields = {}

      const response = yield call(api.settings.deleteRole, params, fields)
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(settingActions.deleteRoleSuccess(data['result'][0]))
        },
        function* onFail(key, message, code) {
          yield put(settingActions.deleteRoleFailure(message, code))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* currencyRead({}) {
    try {
      const params = {}
      const fields = {default: true}

      const response = yield call(api.settings.currencyRead, params, fields)
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(settingActions.currencyReadSuccess(data['result']))
        },
        function* onFail(key, message, code) {
          yield put(settingActions.currencyReadFailure(message, code))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* currencyUpdate({currencyId, rate, currencyName}) {
    try {
      const params = {
        currencyId,
        rate,
        currencyName,
      }

      const fields = {}

      const response = yield call(api.settings.currencyUpdate, params, fields)
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(settingActions.currencyUpdateSuccess(data['result']))
          yield put(snackbarMessage(`Currency ${currencyName} Rate Updated`))
        },
        function* onFail(key, message, code) {
          yield put(settingActions.currencyUpdateFailure(message, code))
          yield put(snackbarMessage(`Currency ${currencyName} Rate failed to update`))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* getGlobalCurrencies({}) {
    try {
      const params = {}
      const fields = {}

      const response = yield call(api.settings.getGlobalCurrencies, params, fields)
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(settingActions.getGlobalCurrenciesSuccess(data['result']))
        },
        function* onFail(key, message, code) {
          yield put(settingActions.getGlobalCurrenciesFailure(message, code))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* taxCodeUpdate({taxCode}) {
    try {
      const {id, rate, code, accountingSalesCode, accountingPurchaseCode} = taxCode

      const params = {
        id,
        rate,
        code,
        accountingSalesCode,
        accountingPurchaseCode,
      }

      const fields = {}

      const response = yield call(api.taxCode.update, params, fields)
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(settingActions.taxCodeUpdateSuccess(data.result))
          yield put(snackbarMessage(`Tax Rate ${code} updated`, iconNames.tick))
        },
        function* onFail(key, message, code) {
          yield put(settingActions.taxCodeUpdateFailure(message, code))
          yield put(snackbarMessage(`Tax Rate ${code} failed to update`))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* currencyEnable({currencies}) {
    try {
      const params = {
        currencies,
      }
      const fields = {}

      const response = yield call(api.settings.currencyEnable, params, fields)
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(settingActions.currencyEnableSuccess(data['result']))
          if (currencies.length > 0) {
            yield put(snackbarMessage('Currencies Added'))
          }
        },
        function* onFail(key, message, code) {
          yield put(settingActions.currencyEnableFailure(message, code))
          yield put(snackbarMessage('Currencies failed to add'))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* taxCodeSetDefault({id, code}) {
    try {
      const params = {
        id,
      }

      const fields = {}

      const response = yield call(api.taxCode.setDefault, params, fields)
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(settingActions.taxCodeUpdateSuccess(data.result))
          yield put(snackbarMessage(`Tax Rate ${code} set default`, iconNames.tick))
        },
        function* onFail(key, message, code) {
          yield put(settingActions.taxCodeUpdateFailure(message, code))
          yield put(snackbarMessage(`Tax Rate ${code} couldn't be set as default`))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* currencyRemoteUpdate({}) {
    try {
      const params = {}
      const fields = {}

      const response = yield call(api.settings.currencyRemoteUpdate, params, fields)
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(settingActions.currencyRemoteUpdateSuccess(data['result']))
          yield put(snackbarMessage('Currencies have updated to latest exchange rates'))
        },
        function* onFail(key, message, code) {
          yield put(settingActions.currencyRemoteUpdateFailure(message, code))
          yield put(snackbarMessage('Currencies failed to update to the latest exchange rates'))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* addActivityType({name, resourceTypeId, itemType}) {
    try {
      const params = {name, resourceTypeId, itemType}
      const fields = {default: true}

      const response = yield call(api.activityTypes.add, params, fields)
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(settingActions.activityTypeAddSuccess(data))
          yield put(snackbarMessage(`Activity type ${name} added`, iconNames.tick))
        },
        function* onFail(key, message, code) {
          yield put(settingActions.activityTypeAddFailure(message, code))
          yield put(snackbarMessage(`Activity type ${name} failed to add`))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* addActivityResourceType({name}) {
    try {
      const params = {name}
      const fields = {default: true}

      const response = yield call(api.activityResourceTypes.add, params, fields)
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(settingActions.activityResourceTypeAddSuccess(data))
          yield put(snackbarMessage(`Activity resource type ${name} added`, iconNames.tick))
        },
        function* onFail(key, message, code) {
          yield put(settingActions.activityResourceTypeAddFailure(message, code))
          yield put(snackbarMessage(`Activity resource type ${name} failed to add`))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* addActivityResource({name, typeId}) {
    try {
      const params = {name, typeId}
      const fields = {default: true}

      const response = yield call(api.activityResources.add, params, fields)
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(settingActions.activityResourceAddSuccess(data))
          yield put(snackbarMessage(`Activity resource ${name} added`, iconNames.tick))
        },
        function* onFail(key, message, code) {
          yield put(settingActions.activityResourceAddFailure(message, code))
          yield put(snackbarMessage(`Activity resource ${name} failed to add`))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* activityTypeUpdate({id, name, resourceTypeId}) {
    try {
      const params = {id, name, resourceTypeId}
      const fields = {default: true}

      const response = yield call(api.activityTypes.update, params, fields)
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(settingActions.activityTypeUpdateSuccess(data))
          yield put(snackbarMessage(`Activity type ${name} updated`, iconNames.tick))
        },
        function* onFail(key, message, code) {
          yield put(settingActions.activityTypeUpdateFailure(message, code))
          yield put(snackbarMessage(`Activity type ${name} failed to save`))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* activityResourceTypeUpdate({id, name}) {
    try {
      const params = {id, name}
      const fields = {default: true}

      const response = yield call(api.activityResourceTypes.update, params, fields)
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(settingActions.activityResourceTypeUpdateSuccess(data))
          yield put(snackbarMessage(`Activity resource type ${name} saved`, iconNames.tick))
        },
        function* onFail(key, message, code) {
          yield put(settingActions.activityResourceTypeUpdateFailure(message, code))
          yield put(snackbarMessage(`Activity resource type ${name} failed to save`))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* activityResourceUpdate({id, name}) {
    try {
      const params = {id, name}
      const fields = {default: true}

      const response = yield call(api.activityResources.update, params, fields)
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(settingActions.activityResourceUpdateSuccess(data))
          yield put(snackbarMessage(`Activity resource ${name} saved`, iconNames.tick))
        },
        function* onFail(key, message, code) {
          yield put(settingActions.activityResourceUpdateFailure(message, code))
          yield put(snackbarMessage(`Activity resource ${name} failed to save`))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* activityTypeDelete({id, name}) {
    try {
      const params = {id}
      const fields = {default: true}

      const response = yield call(api.activityTypes.delete, params, fields)
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(settingActions.activityTypeDeleteSuccess(data))
          yield put(snackbarMessage(`Activity type ${name} removed`, iconNames.tick))
        },
        function* onFail(key, message, code) {
          yield put(settingActions.activityTypeDeleteFailure(message, code))
          yield put(snackbarMessage(`Activity type ${name} failed to be removed`))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* activityResourceTypeDelete({id, name}) {
    try {
      const params = {id}
      const fields = {default: true}

      const response = yield call(api.activityResourceTypes.delete, params, fields)
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(settingActions.activityResourceTypeDeleteSuccess(data))
          yield put(snackbarMessage(`Activity resource type ${name} deleted`, iconNames.tick))
        },
        function* onFail(key, message, code) {
          yield put(settingActions.activityResourceTypeDeleteFailure(message, code))
          yield put(snackbarMessage(`Activity resource type ${name} failed to be removed`))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* activityResourceDelete({id, name}) {
    try {
      const params = {id}
      const fields = {default: true}

      const response = yield call(api.activityResources.delete, params, fields)
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(settingActions.activityResourceDeleteSuccess(data))
          yield put(snackbarMessage(`Activity resource ${name} deleted`, iconNames.tick))
        },
        function* onFail(key, message, code) {
          yield put(settingActions.activityResourceDeleteFailure(message, code))
          yield put(snackbarMessage(`Activity resource ${name} failed to be removed`))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* userSettingUpdate({setting, value, name, successMessage, errorMessage, onSuccess}) {
    try {
      const params = {setting, value}
      const fields = {default: true}

      if (!successMessage)
        successMessage = `${name} updated`

      if (!errorMessage)
        errorMessage = `${name} failed to save`

      const response = yield call(api.accountSettings.update, params, fields)
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(settingActions.accountSettingUpdateSuccess(data.result))
          yield put(snackbarMessage(successMessage, iconNames.tick))
          if (onSuccess) onSuccess()
        },
        function* onFail(key, message, code) {
          yield put(settingActions.accountSettingUpdateFailure(message, code))
          yield put(snackbarMessage(errorMessage))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* userSettingsUpdate({settings, description}) {
    try {
      var success = true
      for (const setting of settings) {
        const fields = {default: true}
        const response = yield call(api.accountSettings.update, setting, fields)
        yield api.handleResponse(
          response,
          function* onOk(key, data) {
            yield put(settingActions.accountSettingUpdateSuccess(data.result))
          },
          function* onFail(key, message, code) {
            yield put(settingActions.accountSettingUpdateFailure(message, code))
            success = false
          }
        )
      }

      if (success) {

        yield put(snackbarMessage(`${description} updated`, iconNames.tick))
      } else {
        yield put(snackbarMessage(`${description} failed to save`))
      }

    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* referralFeeClear({}) {
    try {
      const params = {}
      const fields = {}

      const response = yield call(api.settings.referralFeeClear, params, fields)
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(settingActions.referralFeeClearSuccess(data.result[0]))
          yield put(snackbarMessage('Default company referral fee has now been cleared'))
        },
        function* onFail(key, message, code) {
          yield put(settingActions.referralFeeClearFailure(message, code))
          yield put(snackbarMessage('Default company referral fee clear failure'))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* referralFeeApply({referralCompanyId, referralValue, referralType, coverReferral}) {
    try {
      const params = {referralCompanyId, referralValue, referralType, coverReferral}
      const fields = {default: true}

      const response = yield call(api.settings.referralFeeApply, params, fields)
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(settingActions.referralFeeApplySuccess(data.result[0]))
          yield put(snackbarMessage('Default company referral fee has now been set'))
        },
        function* onFail(key, message, code) {
          yield put(settingActions.referralFeeApplyFailure(message, code))
          yield put(snackbarMessage('Default company referral fee set failure'))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* referralFeeRead({}) {
    try {
      const params = {}
      const fields = {default: true}

      const response = yield call(api.settings.referralFeeRead, params, fields)
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(settingActions.referralFeeReadSuccess(data.result))
        },
        function* onFail(key, message, code) {
          yield put(settingActions.referralFeeReadFailure(message, code))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* userSettingsRead({}) {
    try {
      const params = {}
      const fields = {default: true}

      const response = yield call(api.accountSettings.get, params, fields)
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(settingActions.accountSettingsReadSuccess(data.result))
        },
        function* onFail(key, message, code) {
          yield put(settingActions.accountSettingsReadFailure(message, code))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* authSettingsRead({}) {
    try {
      const response = yield call(api.authSettings.get)
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(settingActions.authSettingsReadSuccess(data.result))
        },
        function* onFail(key, message, code) {
          yield put(settingActions.authSettingsReadFailure(message, code))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* authSettingsUpdate({settings, snackBarMessage}) {

    const params = {settings}
    try {
      const response = yield call(api.authSettings.update, params)
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(settingActions.authSettingsUpdateSuccess(data.result))
          yield put(snackbarActions.snackbarMessage(snackBarMessage, iconNames.tick))
        },
        function* onFail(key, message, code) {
          yield put(settingActions.authSettingsUpdateFailure(message, code))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* authUserSettingsRead() {
    try {
      const params = {isUser: false}  // TODO: can be removed once CG-493 is complete
      const response = yield call(api.authUserSettings.get, params)
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(settingActions.authUserSettingsReadSuccess(data.result))
        },
        function* onFail(key, message, code) {
          yield put(settingActions.authUserSettingsReadFailure(message, code))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* getTfaQRCode({jwtToken}) {

    try {
      const response = yield call(api.tfaSetting.getCode, {jwtToken})
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(settingActions.getTfaQRCodeSuccess(data?.result))

        },
        function* onFail(key, message, code) {
          yield put(settingActions.getTfaQRCodeFailure(message, code))
          yield put(snackbarMessage(message))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* enableTfa({code, deviceFingerprint, isTrustDevice}) {

    const params = {code, deviceFingerprint, trustDevice: isTrustDevice}
    try {
      const response = yield call(api.tfaSetting.enable, params)
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(settingActions.enableTfaSuccess(data?.result))
          yield put(snackbarMessage("2FA successfully enabled", iconNames.tick))
          yield put(closeTfaSetup())
          yield put(settingActions.authUserSettingsRead())
          yield put(pivotTableActions.requestData(trustedDevicesPivotTableKey))
        },
        function* onFail(key, message, code) {
          yield put(settingActions.enableTfaFailure(message, code))
          yield put(snackbarMessage(message))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* disableTfaForUser({accessToken}) {
    try {
      yield put(snackbarMessage("Disabling 2FA...", iconNames.loading, false, false))
      const response = yield call(api.tfaSetting.disableForUser, {accessToken})
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(settingActions.disableTfaForUserSuccess(data?.result))
          yield put(snackbarMessage("2FA successfully disabled", iconNames.tick))
          yield put(settingActions.authUserSettingsRead())
          yield put(pivotTableActions.requestData(trustedDevicesPivotTableKey))
        },
        function* onFail(key, message, code) {
          yield put(settingActions.disableTfaForUserFailure(message, code))
          yield put(snackbarMessage(message))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* disableTfaForAll({accessToken}) {
    try {
      yield put(snackbarMessage("Disabling 2FA...", iconNames.loading, false, false))
      const response = yield call(api.tfaSetting.disableForAll, {accessToken})
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(settingActions.disableTfaForAllSuccess(data?.result))
          yield put(snackbarMessage("2FA successfully disabled", iconNames.tick))
          yield put(settingActions.authSettingsRead())
          yield put(settingActions.authUserSettingsRead())
          yield put(pivotTableActions.requestData(trustedDevicesPivotTableKey))
        },
        function* onFail(key, message, code) {
          yield put(settingActions.disableTfaForAllFailure(message, code))
          yield put(snackbarMessage(message))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* prefixesRead({}) {
    try {
      const params = {}
      const fields = {default: true}

      const response = yield call(api.prefixes.get, params, fields)
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(settingActions.prefixReadSuccess(data.result))
        },
        function* onFail(key, message, code) {
          yield put(settingActions.prefixReadFailure(message, code))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* prefixUpdate({prefix}) {
    const {typeId, value, name, contactId = null} = prefix
    const params = {
      typeId,
      value,
      contactId,
    }
    const fields = {default: true}
    const response = yield call(api.prefixes.update, params, fields)
    yield api.handleResponse(
      response,
      function* onOk(key, data) {
        if (isEmpty(value)) {
          yield put(settingActions.prefixUpdateSuccess({...data.result, value: null}))
        } else {
          yield put(settingActions.prefixUpdateSuccess(data.result))
        }
        yield put(snackbarMessage(`${name} updated`, iconNames.tick))
      },
      function* onFail(key, message, code) {
        yield put(settingActions.prefixUpdateFailure(message, code))
        yield put(snackbarMessage(message))
      }
    )
  }

  function* sendAddressAdd({email, domain, name}) {
    const params = {
      email,
      domain,
      name,
    }
    const fields = {default: true}
    const response = yield call(api.sendAddresses.add, params, fields)
    yield api.handleResponse(
      response,
      function* onOk(key, data) {
        yield put(settingActions.sendAddressAddSuccess(data.result[0]))
        yield put(snackbarMessage(`Send address ${email}@${domain} added`, iconNames.tick))
      },
      function* onFail(key, message, code) {
        yield put(settingActions.sendAddressAddFailure(message, code))
        yield put(snackbarMessage(`${email} add failed`))
      }
    )
  }

  function* sendAddressDelete({address}) {
    const params = {
      address,
    }
    const fields = {default: true}
    const response = yield call(api.sendAddresses.delete, params, fields)
    yield api.handleResponse(
      response,
      function* onOk(key, data) {
        yield put(settingActions.sendAddressDeleteSuccess(data.result[0]))
        yield put(snackbarMessage(`${address} deleted`, iconNames.tick))
      },
      function* onFail(key, message, code) {
        yield put(settingActions.sendAddressDeleteFailure(message, code))
        yield put(snackbarMessage(`${address} delete failed`))
      }
    )
  }

  function* sendAddressValidate({address}) {
    const params = {
      address,
    }
    const fields = {default: true}
    const response = yield call(api.sendAddresses.validate, params, fields)
    yield api.handleResponse(
      response,
      function* onOk(key, data) {
        yield put(settingActions.sendAddressValidateSuccess(data.result[0]))
        yield put(snackbarMessage(`Resent validation ${address}`, iconNames.tick))
      },
      function* onFail(key, message, code) {
        yield put(settingActions.sendAddressValidateFailure(message, code))
        yield put(snackbarMessage(`Couldn't resend validation ${address}`))
      }
    )
  }

  function* sendAddressSetDefault({address}) {
    const params = {
      address,
    }
    const fields = {default: true}
    const response = yield call(api.sendAddresses.setDefault, params, fields)
    yield api.handleResponse(
      response,
      function* onOk(key, data) {
        yield put(settingActions.sendAddressSetDefaultSuccess(data.result[0]))
        yield put(snackbarMessage(`${address} is now default`, iconNames.tick))
      },
      function* onFail(key, message, code) {
        yield put(settingActions.sendAddressSetDefaultFailure(message, code))
        yield put(snackbarMessage(`Couldn't set ${address} as default`))
      }
    )
  }

  function* contactImportAutoDetectColumns({}) {
    try {

      const user = yield select(state => getUser(state))
      const job = yield select(state => state.settingsContext.contactImport.job)

      if (job) {
        // Auto-detect the columns from the header
        const {header, fields} = job

        if (header) {

          // The fields we can choose from
          const {contactFields, companyFields, countries} = yield select(state => state.data)
          const allFields = getAllFields(
            contactFields,
            companyFields,
            true,
            countries,
            null,
            false,
            user).filter(field => !field.readOnly)

          // Ensure we have a correct field array
          const newFields = [...fields]
          while (newFields.length < header.length) newFields.push(null)

          // 1st pass: Detect and allocate fields on label
          for (let i = 0; i < header.length; i++) {
            if (newFields[i] === null) newFields[i] = findField(header[i], allFields, newFields, false)
          }

          // 2nd pass: Detect and allocate fields on key
          for (let i = 0; i < header.length; i++) {
            if (newFields[i] === null) newFields[i] = findField(header[i], allFields, newFields, true)
          }

          // Re-validate with the new fields
          const newJob = {...job, fields: newFields}
          yield put(settingActions.contactImportJobValidate(newJob))
        }
      }
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* contactImportJobRead({}) {
    try {
      const response = yield call(api.contactImportJob.get, {}, {default: true})

      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(settingActions.contactImportJobReadSuccess(data.result[0]))
        },
        function* onFail(key, message, code) {
          if (code === apiErrorCodes.NO_RESULTS_FOUND) {
            yield put(settingActions.contactImportJobReadSuccess(null))
          } else {
            yield put(settingActions.contactImportJobReadFailure(message, code))
          }
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* contactImportJobUpload({file, onProgress}) {
    try {
      yield put(snackbarMessage('Uploading your import file...', 'loading', false, false))

      const data = getFileFormData(file)

      const config = getFileFormConfig(progress => {
        if (onProgress) {
          const percent = (progress.loaded * 100) / progress.total
          onProgress(percent)
        }
      })

      const response = yield call(api.contactImportJob.uploadFile, data, config)

      if (response.ok) {
        yield put(settingActions.contactImportJobUploadSuccess())
        yield put(settingActions.contactImportJobRead())
        yield put(snackbarClear())
        const skill = yield select(state => state.onboarding.skills.IMPORT_CONTACTS)
        if (skill.active) yield put(onboardingActions.skillPage(SKILLS.IMPORT_CONTACTS.key, 2))
      } else {
        const problem = response.headers['error'] || response.problem
        yield put(settingActions.contactImportJobUploadFailure(problem))
        yield put(snackbarMessage('Your file could not be uploaded: ' + problem, 'error', false))
      }
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* contactImportJobUpdate({job, updateData = true}) {
    try {
      const params = {importJob: job}
      const response = yield call(api.contactImportJob.update, params, {default: true})

      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(settingActions.contactImportJobUpdateSuccess(data.result[0]))
          if (updateData) yield put(pivotTableActions.requestData(contactImportPivotTableKey))
        },
        function* onFail(key, message, code) {
          yield put(settingActions.contactImportJobUpdateFailure(message, code))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* contactImportJobValidate({job}) {
    try {
      const params = {importJob: job}
      const response = yield call(api.contactImportJob.validate, params, {default: true})

      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(settingActions.contactImportJobValidateSuccess(data.result[0]))
        },
        function* onFail(key, message, code) {
          yield put(settingActions.contactImportJobValidateFailure(message, code))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* contactImportJobPreCommit({job}) {
    try {
      const params = {importJob: job}
      const response = yield call(api.contactImportJob.preCommit, params, {default: true})

      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(settingActions.contactImportJobPreCommitSuccess(data.result[0]))

        },
        function* onFail(key, message, code) {
          yield put(settingActions.contactImportJobPreCommitFailure(message, code))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* contactImportJobCommit({job}) {
    try {
      const params = {importJob: {...job, createList: true}}
      const response = yield call(api.contactImportJob.commit, params, {default: true})

      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(settingActions.contactImportJobCommitSuccess(data.result[0]))
        },
        function* onFail(key, message, code) {
          yield put(settingActions.contactImportJobCommitFailure(message, code))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* contactImportJobReset({}) {
    try {
      const response = yield call(api.contactImportJob.reset, {}, {default: true})

      yield api.handleResponse(
        response,
        function* onOk() {
          yield put(settingActions.contactImportJobResetSuccess(null))
        },
        function* onFail(key, message, code) {
          if (code === apiErrorCodes.NO_RESULTS_FOUND) {
            yield put(settingActions.contactImportJobResetSuccess(null))
          } else {
            yield put(settingActions.contactImportJobResetFailure(message, code))
          }
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* setCurrentModule({module}) {
    try {
      yield call(api.settings.setCurrentModule, module)
    } catch (e) {
      console.error(e)
    }
  }

  function* loadTeams() {
    try {
      const params = {}
      const fields = {
        default: true,
      }

      const response = yield call(api.teams.get, params, fields)

      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(loadTeamsSuccess(data.result))
        },
        function* onFail(key, message, code) {
          yield put(loadTeamsFailure(message, code))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* addTeam({parentTeamId, name}) {
    try {
      yield put(snackbarMessage('Adding your team', 'loading', false, false))
      const response = yield call(api.teams.add, {parentTeamId, name})

      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          const team = data.result[0]
          yield put(addTeamSuccess(team))
          yield put(snackbarMessage('Your team has been added', iconNames.tick))
        },
        function* onFail(key, message, code) {
          yield put(addTeamFailure(message, code))
          yield put(snackbarMessage('Sorry, your team could not be added', iconNames.error, false, true))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* updateTeam({team}) {
    try {
      yield put(snackbarMessage('Updating your team', 'loading', false, false))
      const response = yield call(api.teams.update, {team})

      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          const team = data.result[0]
          yield put(updateTeamSuccess(team))
          yield put(snackbarMessage('Your team has been updated', iconNames.tick))
        },
        function* onFail(key, message, code) {
          yield put(updateTeamFailure(message, code))
          yield put(snackbarMessage('Sorry, your team could not be updated', iconNames.error, false, true))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* deleteTeam({teamId}) {
    try {
      yield put(snackbarMessage('Deleting your team', 'loading', false, false))
      const response = yield call(api.teams.delete, {teamId})

      yield api.handleResponse(
        response,
        function* onOk() {
          yield put(deleteTeamSuccess(teamId))
          yield put(snackbarMessage('Your team has been deleted', iconNames.tick))
        },
        function* onFail(key, message, code) {
          yield put(deleteTeamFailure(message, code))
          yield put(snackbarMessage('Sorry, your team could not be deleted', iconNames.error, false, true))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* loadTeamMembers() {
    try {
      const params = {}
      const fields = {
        default: true,
      }

      const response = yield call(api.teamMembers.get, params, fields)

      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(loadTeamMembersSuccess(data.result))
        },
        function* onFail(key, message, code) {
          yield put(loadTeamMembersFailure(message, code))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* updateTeamMember({teamMember}) {
    try {
      const response = yield call(api.teamMembers.update, {teamMember})

      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(updateTeamMemberSuccess(data.result))
        },
        function* onFail(key, message, code) {
          yield put(updateTeamMemberFailure(message, code))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* sendResetPasswordEmail({userId}) {

    try {

      const response = yield call(api.settings.resetPassword, userId)
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(sendResetPasswordEmailSuccess(data))
          yield put(snackbarMessage('Reset password email sent', iconNames.tick))
        },
        function* onFail(key, message, code) {
          yield put(sendResetPasswordEmailFailure(message, code))
          yield put(snackbarMessage(message, iconNames.error, true, false))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* addUser({companyId, contactId, selectedFeatureIds, selectedPackages}) {
    try {
      const params = {
        companyId,
        contactId,
        licences: selectedFeatureIds,
        packages: selectedPackages.map(selectedPackage => selectedPackage.id),
      }
      yield put(snackbarMessage('Adding new user', iconNames.loading, false, false))
      const response = yield call(api.settings.addUser, params)
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(addUserSuccess(data.result[0]))
          const user = yield select(getUser)

          if (isTrialAccountType(user)) {
            const featurePackages = yield select(getPackages)
            const accountPackages = yield select(getAccountPackages)
            const contacts = yield select(getContacts)

            const filteredAccountPackages = getFilteredAccountPackages(accountPackages, selectedPackages)
            if (isSubscriptionUpdateRequiredForTrial(filteredAccountPackages, featurePackages, contacts)) {
              const params = getSubscriptionParamsForTrial(
                user,
                accountPackages,
                featurePackages,
                filteredAccountPackages
              )
              yield api.subscriptions.update(params).then(response)
            }
          }

          const addUserDialog = yield select(getAddUserDialog)
          const isAddUserDialogVisible = get(addUserDialog, 'isVisible', false)
          if (isAddUserDialogVisible)
            yield put(hideAddUserDialog())

          const addExternalUserDialog = yield select(getAddExternalUserDialog)
          const isAddExternalDialogVisible = get(addExternalUserDialog, 'isVisible', false)
          if (isAddExternalDialogVisible)
            yield put(hideAddExternalUserDialog())

          yield put(snackbarMessage('User Added', iconNames.tick, true, false))
        },
        function* onFail(key, message, code) {
          yield put(addUserFailure(message, code))
          yield put(snackbarMessage(message, iconNames.error, true, false))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* updateUsers() {
    try {
      yield delay(USER_UPDATE_DEBOUNCE_DELAY)

      const internalContacts = yield select(getInternalUsers)
      const externalContacts = yield select(getExternalUsers)
      const allContacts = getAllContacts(internalContacts, externalContacts)
      const dirtyContacts = getDirtyContacts(allContacts)

      if (isEmpty(dirtyContacts))
        return

      yield all(dirtyContacts.map(contact =>
        call(updateUserFromContact, contact)))

    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* updateUserFromContact(contact) {
    try {
      if (isNil(contact) || isNil(contact.userId))
        return

      const params = {
        userId: contact.userId,
        licences: getLicenceIds(getUserLicences(contact)),
        packages: getPackageIds(getUserPackages(contact)),
      }

      const response = yield call(api.settings.updateUser, params)
      yield api.handleResponse(
        response,
        function* onOk() {
          yield put(updateUserSuccess(contact.userId))
        },
        function* onFail(key, message, code) {
          yield put(updateUserFailure(contact.userId, message, code))
        }
      )

    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* recalculateUserLoginsFromUserUpdate({userId, licences, packages}) {
    try {

      const roles = yield select(state => state.settingsContext.roles)
      const permissions = yield select(getPermissions)
      const currentLicences = yield select(getLicences)
      const currentPackages = yield select(getPackagesFromSettings)
      const internalContacts = yield select(getInternalUsers)
      const externalContacts = yield select(getExternalUsers)

      const updatedInternalContacts = updateContactsLicencesAndPackages(
        internalContacts,
        userId,
        licences,
        packages,
        Object.values(currentPackages)
      )

      const updatedExternalContacts = updateContactsLicencesAndPackages(
        externalContacts,
        userId,
        licences,
        packages,
        Object.values(currentPackages)
      )

      const allContacts = getAllContacts(updatedInternalContacts, updatedExternalContacts)
      const updatedLicences = updateLicences(currentLicences, allContacts)
      const updatedPackages = updatePackages(currentPackages, allContacts)

      const updatedLoginsData = {
        roles,
        permissions,
        licences: updatedLicences,
        packages: updatedPackages,
        contacts: updatedInternalContacts,
        externalContacts: updatedExternalContacts,
      }

      yield put(setLicences(updatedLoginsData))

    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* removeUser({contactId}) {
    try {

      const params = {
        contactId,
      }

      yield put(snackbarMessage('Deleting user', iconNames.loading, false, false))
      const response = yield call(api.settings.removeUser, params)
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(removeUserSuccess(data.result[0]))
          yield put(snackbarMessage('User Removed', iconNames.tick, true, false))
        },
        function* onFail(key, message, code) {
          yield put(removeUserFailure(message, code))
          yield put(snackbarMessage(message, iconNames.error, true, false))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* loadDocumentFolderTemplates({documentFolderTemplateType}) {
    try {
      const params = {
        type: documentFolderTemplateType,
        sortColumn: 'name',
      }

      const fields = {
        default: true,
      }

      const response = yield call(api.documentFolderTemplates.get, params, fields)
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(loadDocumentFolderTemplatesSuccess(data.result))
        },
        function* onFail(key, message, code) {
          yield put(loadDocumentFolderTemplatesFailure(message, code))
          yield put(snackbarMessage(message, iconNames.error, true, false))
        }
      )

    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* addDocumentFolderTemplate({documentFolderTemplate}) {
    try {
      const response = yield call(api.documentFolderTemplates.add, {documentFolderTemplate})
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(addDocumentFolderTemplateSuccess(data.result[0]))
        },
        function* onFail(key, message, code) {
          yield put(addDocumentFolderTemplateFailure(message, code))
          yield put(snackbarMessage(message, iconNames.error, true, false))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* updateDocumentFolderTemplate({documentFolderTemplate}) {
    try {
      const response = yield call(api.documentFolderTemplates.update, {documentFolderTemplate})
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(updateDocumentFolderTemplateSuccess(data.result))
        },
        function* onFail(key, message, code) {
          yield put(updateDocumentFolderTemplateFailure(message, code))
          yield put(snackbarMessage(message, iconNames.error, true, false))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* deleteDocumentFolderTemplate({id}) {
    try {
      const response = yield call(api.documentFolderTemplates.delete, {id})
      yield api.handleResponse(
        response,
        function* onOk() {
          yield put(deleteDocumentFolderTemplateSuccess(id))
        },
        function* onFail(key, message, code) {
          yield put(deleteDocumentFolderTemplateFailure(message, code))
          yield put(snackbarMessage(message, iconNames.error, true, false))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* deleteTrustedDevice({device}) {
    try {
      const deviceId = get(device, 'id', null)
      const userId = get(device, 'userId', null)
      const response = yield call(api.trustedDeviceData.delete, {deviceId, userId}, {})
      yield api.handleResponse(
        response,
        function* onOk() {
          yield put(deleteTrustedDeviceSuccess(deviceId))
          yield put(snackbarMessage('Trusted device deleted', iconNames.tick, true, false))
          yield put(pivotTableActions.requestData(trustedDevicesPivotTableKey))
        },
        function* onFail(key, message, code) {
          yield put(deleteTrustedDeviceFailure(message, code))
          yield put(snackbarMessage(message, iconNames.error, true, false))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* dismissTfaPrompt() {
    try {
      const response = yield call(api.tfaSetting.dismiss)
      yield api.handleResponse(
        response,
        function* onOK(key, data) {
          yield put(dismissTfaPromptSuccess(data.result))
        },
        function* onFail(key, message, code) {
          yield put(dismissTfaPromptFailure(message, code))
        })
    } catch (e) {
      console.error(e)
      yield put(generalError())
    }
  }

  function* resetTfa({accessToken, userId}) {
    try {
      yield put(snackbarMessage('Resetting 2FA', iconNames.loading, false, false))
      const response = yield call(api.tfaSetting.reset, {accessToken, userId})
      yield api.handleResponse(
        response,
        function* onOk() {
          yield put(resetTfaSuccess(userId))
          yield put(snackbarMessage('2FA has been reset', iconNames.tick, true, true))
        },
        function* onFail(key, message, code) {
          yield put(resetTfaFailure(message, code))
          yield put(snackbarMessage(message, iconNames.error, true, true))
        })
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* loadSupportTokens() {
    try {
      const response = yield call(api.supportTokens.get)
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(loadSupportTokensSuccess(data.result))
        },
        function* onFail(key, message, code) {
          yield put(loadSupportTokensFailure(message, code))
          yield put(generalError(message, code))
        })
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* addSupportToken({accessToken, expiryMillis}) {
    try {
      const response = yield call(api.supportTokens.add, {accessToken, expiryMillis})
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(addSupportTokenSuccess(data.result))
        },
        function* onFail(key, message, code) {
          yield put(addSupportTokenFailure(message, code))
          yield put(generalError(message, code))
        })
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* deleteSupportToken({token}) {
    try {
      const response = yield call(api.supportTokens.delete, {token})
      yield api.handleResponse(
        response,
        function* onOk() {
          yield put(deleteSupportTokenSuccess(token))
        },
        function* onFail(key, message, code) {
          yield put(deleteSupportTokenFailure(message, code))
          yield put(generalError(message, code))
        })
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }
  const automaticReminderConfigDataLoadMap = (el) => ({...el, value: el.days})

  function* automaticReminderConfigsRead({typeId}) {
    try {
      const response = yield call(api.automaticReminderConfigurations.get, {type: typeId})
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(readAutomaticReminderConfigsSuccess(data.result.map(automaticReminderConfigDataLoadMap)))
        },
        function* onFail(key, message, code) {
          yield put(readAutomaticReminderConfigsFailure(message, code))
          yield put(snackbarMessage(message, iconNames.error))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* automaticReminderConfigAdd({days, typeId}) {
    try {
      const params = {reminderConfig: {days, type: typeId, id: null}}
      const addResponse = yield call(api.automaticReminderConfigurations.add, params)
      yield api.handleResponse(
        addResponse,
        function* onOk() {
          const getResponse = yield call(api.automaticReminderConfigurations.get, {type: typeId})
          yield api.handleResponse(
            getResponse,
            function* onOk(key, data) {
              yield put(addAutomaticReminderConfigSuccess(data.result.map(automaticReminderConfigDataLoadMap)))
              yield put(snackbarMessage('Reminder Added', iconNames.tick))
            },
            function* onFail(key, message, code) {
              yield put(addAutomaticReminderConfigFailure(message, code))
              yield put(snackbarMessage(message, iconNames.error))
            }
          )
        },
        function* onFail(key, message, code) {
          yield put(addAutomaticReminderConfigFailure(message, code))
          yield put(snackbarMessage(message, iconNames.error))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* automaticReminderConfigUpdate({id, days, typeId}) {
    try {
      const params = {reminderConfig: {id, days, type: typeId}}
      const response = yield call(api.automaticReminderConfigurations.update, params)
      yield api.handleResponse(
        response,
        function* onOk(key, data) {
          yield put(updateAutomaticReminderConfigSuccess({id, days, result: data.result}))
          yield put(snackbarMessage('Reminder Updated', iconNames.tick))
        },
        function* onFail(key, message, code) {
          yield put(updateAutomaticReminderConfigFailure(message, code))
          yield put(snackbarMessage(message, iconNames.error))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* automaticReminderConfigDelete({id, typeId}) {
    try {
      const deleteResponse = yield call(api.automaticReminderConfigurations.delete, {id, type: typeId})
      yield api.handleResponse(
        deleteResponse,
        function* onOk() {
          const getResponse = yield call(api.automaticReminderConfigurations.get, {type: typeId})
          yield api.handleResponse(
            getResponse,
            function* onOk(key, data) {
              yield put(deleteAutomaticReminderConfigSuccess(data.result.map(automaticReminderConfigDataLoadMap)))
              yield put(snackbarMessage('Reminder Deleted', iconNames.tick))
            },
            function* onFail(key, message, code) {
              yield put(deleteAutomaticReminderConfigFailure(message, code))
              yield put(snackbarMessage(message, iconNames.error))
            }
          )
        },
        function* onFail(key, message, code) {
          yield put(deleteAutomaticReminderConfigFailure(message, code))
          yield put(snackbarMessage(message, iconNames.error))
        }
      )
    } catch (e) {
      console.error(e)
      yield put(generalError(e.message))
    }
  }

  function* myWatcher() {
    yield all([
      takeLatest(types.SETTINGS_LICENSE_LOAD_ATTEMPT, loadAccountLicenceInfo),
      takeLatest(types.SETTINGS_SEND_ACTIVATION_EMAIL_ATTEMPT, sendActivationEmail),
      takeLatest(types.SETTINGS_SEND_TFA_SETUP_EMAIL_ATTEMPT, sendTFASetupEmail),
      takeLatest(types.SETTINGS_CHANGE_PASSWORD_ATTEMPT, changePassword),
      takeLatest(types.SETTINGS_USER_ROLE_UPDATE_ATTEMPT, userRoleUpdate),
      takeLatest(types.SETTINGS_UPLOAD_SIGNATURE_ATTEMPT, uploadSignature),
      takeLatest(types.SETTINGS_UPLOAD_LOGO_ATTEMPT, uploadLogo),
      takeLatest(types.SETTINGS_DELETE_SIGNATURE_ATTEMPT, deleteSignature),
      takeLatest(types.SETTINGS_DELETE_LOGO_ATTEMPT, deleteLogo),
      takeLatest(types.SETTINGS_ROLE_PERMISSION_ADD_ATTEMPT, addRolePermission),
      takeLatest(types.SETTINGS_ROLE_PERMISSION_DELETE_ATTEMPT, deleteRolePermission),
      takeLatest(types.SETTINGS_ROLE_ADD_ATTEMPT, addRole),
      takeLatest(types.SETTINGS_ROLE_DELETE_ATTEMPT, deleteRole),
      takeLatest(types.SETTINGS_CURRENCY_READ_ATTEMPT, currencyRead),
      takeLatest(types.SETTINGS_CURRENCY_UPDATE_ATTEMPT, currencyUpdate),
      takeLatest(types.SETTINGS_GLOBAL_CURRENCY_ATTEMPT, getGlobalCurrencies),
      takeLatest(types.SETTINGS_CURRENCY_ENABLE_ATTEMPT, currencyEnable),
      takeLatest(types.SETTINGS_TAX_CODE_UPDATE_ATTEMPT, taxCodeUpdate),
      takeLatest(types.SETTINGS_TAX_CODE_SET_DEFAULT_ATTEMPT, taxCodeSetDefault),
      takeLatest(types.SETTINGS_CURRENCY_REMOTE_UPDATE_ATTEMPT, currencyRemoteUpdate),
      takeLatest(types.SETTINGS_ACTIVITY_TYPE_ADD_ATTEMPT, addActivityType),
      takeLatest(types.SETTINGS_ACTIVITY_RESOURCE_TYPE_ADD_ATTEMPT, addActivityResourceType),
      takeLatest(types.SETTINGS_ACTIVITY_RESOURCE_ADD_ATTEMPT, addActivityResource),
      takeLatest(types.SETTINGS_ACTIVITY_TYPE_UPDATE_ATTEMPT, activityTypeUpdate),
      takeLatest(types.SETTINGS_ACTIVITY_RESOURCE_TYPE_UPDATE_ATTEMPT, activityResourceTypeUpdate),
      takeLatest(types.SETTINGS_ACTIVITY_RESOURCE_UPDATE_ATTEMPT, activityResourceUpdate),
      takeLatest(types.SETTINGS_ACTIVITY_TYPE_DELETE_ATTEMPT, activityTypeDelete),
      takeLatest(types.SETTINGS_ACTIVITY_RESOURCE_TYPE_DELETE_ATTEMPT, activityResourceTypeDelete),
      takeLatest(types.SETTINGS_ACTIVITY_RESOURCE_DELETE_ATTEMPT, activityResourceDelete),
      takeLatest(types.SETTINGS_REFERRAL_TYPE_CLEAR_ATTEMPT, referralFeeClear),
      takeLatest(types.SETTINGS_REFERRAL_TYPE_APPLY_ATTEMPT, referralFeeApply),
      takeLatest(types.SETTINGS_REFERRAL_TYPE_READ_ATTEMPT, referralFeeRead),
      takeLatest(types.SETTINGS_USER_SETTINGS_READ_ATTEMPT, userSettingsRead),
      takeLatest(types.SETTINGS_USER_SETTING_UPDATE_ATTEMPT, userSettingUpdate),
      takeLatest(types.SETTINGS_USER_SETTINGS_UPDATE_ATTEMPT, userSettingsUpdate),
      takeLatest(types.SETTINGS_PREFIXES_READ_ATTEMPT, prefixesRead),
      takeLatest(types.SETTINGS_PREFIX_UPDATE_ATTEMPT, prefixUpdate),
      takeLatest(types.SETTINGS_SEND_ADDRESS_ADD_ATTEMPT, sendAddressAdd),
      takeLatest(types.SETTINGS_SEND_ADDRESS_DELETE_ATTEMPT, sendAddressDelete),
      takeLatest(types.SETTINGS_SEND_ADDRESS_VALIDATE_ATTEMPT, sendAddressValidate),
      takeLatest(types.SETTINGS_SEND_ADDRESS_SET_DEFAULT_ATTEMPT, sendAddressSetDefault),
      takeLatest(types.SETTINGS_CONTACT_IMPORT_AUTO_DETECT_COLUMNS, contactImportAutoDetectColumns),
      takeLatest(types.SETTINGS_CONTACT_IMPORT_JOB_READ_ATTEMPT, contactImportJobRead),
      takeLatest(types.SETTINGS_CONTACT_IMPORT_JOB_UPLOAD_ATTEMPT, contactImportJobUpload),
      takeLatest(types.SETTINGS_CONTACT_IMPORT_JOB_UPDATE_ATTEMPT, contactImportJobUpdate),
      takeLatest(types.SETTINGS_CONTACT_IMPORT_JOB_VALIDATE_ATTEMPT, contactImportJobValidate),
      takeLatest(types.SETTINGS_CONTACT_IMPORT_JOB_PRECOMMIT_ATTEMPT, contactImportJobPreCommit),
      takeLatest(types.SETTINGS_CONTACT_IMPORT_JOB_COMMIT_ATTEMPT, contactImportJobCommit),
      takeLatest(types.SETTINGS_CONTACT_IMPORT_JOB_RESET_ATTEMPT, contactImportJobReset),
      takeLatest(types.SET_CURRENT_MODULE, setCurrentModule),
      takeLatest(types.SETTINGS_LOAD_TEAMS_ATTEMPT, loadTeams),
      takeLatest(types.SETTINGS_ADD_TEAM_ATTEMPT, addTeam),
      takeLatest(types.SETTINGS_UPDATE_TEAM_ATTEMPT, updateTeam),
      takeLatest(types.SETTINGS_DELETE_TEAM_ATTEMPT, deleteTeam),
      takeLatest(types.SETTINGS_LOAD_TEAM_MEMBERS_ATTEMPT, loadTeamMembers),
      takeLatest(types.SETTINGS_UPDATE_TEAM_MEMBER_ATTEMPT, updateTeamMember),
      takeLatest(types.SETTINGS_SEND_RESET_PASSWORD_EMAIL_ATTEMPT, sendResetPasswordEmail),
      takeLatest(types.SETTINGS_ADD_USER_ATTEMPT, addUser),
      takeLatest(types.SETTINGS_UPDATE_USER_ATTEMPT, updateUsers),
      takeLatest(types.SETTINGS_UPDATE_USER_ATTEMPT, recalculateUserLoginsFromUserUpdate),
      takeLatest(types.SETTINGS_REMOVE_USER_ATTEMPT, removeUser),
      takeLatest(types.DOCUMENTS_LOAD_FOLDER_TEMPLATES_ATTEMPT, loadDocumentFolderTemplates),
      takeLatest(types.DOCUMENTS_ADD_FOLDER_TEMPLATE_ATTEMPT, addDocumentFolderTemplate),
      takeLatest(types.DOCUMENTS_UPDATE_FOLDER_TEMPLATE_ATTEMPT, updateDocumentFolderTemplate),
      takeLatest(types.DOCUMENTS_DELETE_FOLDER_TEMPLATE_ATTEMPT, deleteDocumentFolderTemplate),
      takeLatest(types.SETTINGS_AUTH_SETTINGS_READ_ATTEMPT, authSettingsRead),
      takeLatest(types.SETTINGS_AUTH_USER_SETTINGS_READ_ATTEMPT, authUserSettingsRead),
      takeLatest(types.SETTINGS_AUTH_SETTINGS_UPDATE_ATTEMPT, authSettingsUpdate),
      takeLatest(types.TRUSTED_DEVICES_DELETE_ATTEMPT, deleteTrustedDevice),
      takeLatest(types.TFA_GET_QR_CODE_ATTEMPT, getTfaQRCode),
      takeLatest(types.TFA_DISABLE_FOR_ALL_ATTEMPT, disableTfaForAll),
      takeLatest(types.TFA_ENABLE_ATTEMPT, enableTfa),
      takeLatest(types.DISMISS_TFA_PROMPT_ATTEMPT, dismissTfaPrompt),
      takeLatest(types.TFA_RESET_ATTEMPT, resetTfa),
      takeLatest(types.TFA_DISABLE_FOR_USER_ATTEMPT, disableTfaForUser),
      takeLatest(types.SETTINGS_SUPPORT_TOKENS_READ_ATTEMPT, loadSupportTokens),
      takeLatest(types.SETTINGS_SUPPORT_TOKENS_ADD_ATTEMPT, addSupportToken),
      takeLatest(types.SETTINGS_SUPPORT_TOKENS_DELETE_ATTEMPT, deleteSupportToken),
      takeLatest(types.AUTOMATIC_REMINDER_CONFIG_READ_ATTEMPT, automaticReminderConfigsRead),
      takeLatest(types.AUTOMATIC_REMINDER_CONFIG_ADD_ATTEMPT, automaticReminderConfigAdd),
      takeLatest(types.AUTOMATIC_REMINDER_CONFIG_UPDATE_ATTEMPT, automaticReminderConfigUpdate),
      takeLatest(types.AUTOMATIC_REMINDER_CONFIG_DELETE_ATTEMPT, automaticReminderConfigDelete),
    ])
  }

  function* getContactImportJob() {
    const response = yield call(api.contactImportJob.get, {}, {default: true})
    if (response.ok) {
      const job = get(response, 'data.data[0].result[0]')
      if (job) yield put(settingActions.contactImportJobReadSuccess(job))
      return job
    }
    return null
  }

  // deamon thread to watch for completed import job
  function* checkForCompleteContactImportJob() {

    // read the job
    let job = yield getContactImportJob()
    while (job && !job.completed) {
      yield delay(1000)
      job = yield getContactImportJob()
    }

    // emit a 'complete' action
    yield put({type: types.SETTINGS_CONTACT_IMPORT_JOB_COMPLETE})
  }

  // deamon thread to watch for pre-commit process running on server
  function* contactImportJobPreCommitWatcher() {
    try {
      while (yield take(types.SETTINGS_CONTACT_IMPORT_JOB_PRECOMMIT_SUCCESS)) {

        // check for job completion
        const tasks = yield all([fork(checkForCompleteContactImportJob)])

        // wait for job completion
        yield take(types.SETTINGS_CONTACT_IMPORT_JOB_COMPLETE)

        // cancel the task
        yield all(tasks.map(task => cancel(task)))
      }
    } catch (e) {
      console.error(e)
    }
  }

  // deamon thread to watch for commit process running on server
  function* contactImportJobCommitWatcher() {
    try {
      while (yield take(types.SETTINGS_CONTACT_IMPORT_JOB_COMMIT_SUCCESS)) {

        // check for job completion
        const tasks = yield all([fork(checkForCompleteContactImportJob)])

        // wait for job completion
        yield take(types.SETTINGS_CONTACT_IMPORT_JOB_COMPLETE)

        // cancel the task
        yield all(tasks.map(task => cancel(task)))
      }
    } catch (e) {
      console.error(e)
    }
  }

  const contactImportPivotTable = createPivotTableWatcher(
    api.contactImportData,
    contactImportPivotTableKey,
    [
      types.SETTINGS_CONTACT_IMPORT_UPDATE_FILTERS,
      types.SETTINGS_CONTACT_IMPORT_JOB_UPLOAD_SUCCESS,
    ],
    state => ({...state.settingsContext.contactImport.filters})
  )

  const trustedDevicePivotTable = createPivotTableWatcher(
    api.trustedDeviceData,
    trustedDevicesPivotTableKey,
    [],
    () => ({})
  )


  function* watcher() {
    yield fork(myWatcher)
    yield fork(contactImportJobPreCommitWatcher)
    yield fork(contactImportJobCommitWatcher)
    yield fork(contactImportPivotTable)
    yield fork(trustedDevicePivotTable)
  }

  return {
    watcher,
  }
}
